import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';

import { ApiEndpoints } from '../../core/api-endpoints.service';
import { AccessKey, DataRecord, WorkflowState } from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';

type LockerOperation = 'open' | 'lock' | 'position' | 'ping';

interface PositionResult {
  latitude: number;
  longitude: number;
}

@Component({
  selector: 'app-locker-operations',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './locker-operations.component.html',
})
export class LockerOperationsComponent {
  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly call = new ApiCallState();
  protected readonly positionResult = signal<PositionResult | undefined>(undefined);
  protected readonly accessKeyOptions = computed(() =>
    Array.from(this.workflow.accessKeys(), ([id, value]) => ({
      id,
      label: `${String(value['displayName'] ?? value['lockType'] ?? id)} | ${String(value['itemId'] ?? '')}`,
    })),
  );
  protected readonly endpoint = computed(() => {
    if (!this.operation) {
      return `${this.endpoints.baseUrl}/{open|lock|position|ping}`;
    }
    return this.endpoints.url(this.operationPath(this.operation));
  });

  protected selectedId = '';
  protected operation: LockerOperation | '' = '';
  protected accessId = '';
  protected accessKey = '';
  protected keyUserId = '';
  protected systemProvider = '';
  protected latitude = 57.1;
  protected longitude = 11.1;

  protected selectAccessKey(): void {
    const selected = this.workflow.accessKeys().get(this.selectedId);
    if (selected) {
      this.applyAccessKey(selected);
    }
  }

  protected submit(): void {
    if (!this.operation) {
      return;
    }

    const payload = {
      accessId: this.accessId,
      accessKey: this.operation === 'position' ? undefined : this.accessKey,
      keyUser: {
        id: this.keyUserId,
        position: { latitude: this.latitude, longitude: this.longitude },
      },
    };
    const operation = this.operation;
    this.positionResult.set(undefined);
    this.call.start(payload);

    this.http
      .post<DataRecord>(this.endpoints.url(this.operationPath(operation)), payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (operation === 'position') {
            const position = response['lockedSpacePosition'];
            if (isRecord(position)) {
              this.positionResult.set({
                latitude: Number(position['latitude']),
                longitude: Number(position['longitude']),
              });
            }
            this.call.succeed(response);
          } else {
            this.call.succeed({ message: 'Operation processed', response });
          }
        },
        error: (error: unknown) => this.call.fail(error),
      });
  }

  protected mapUrl(position: PositionResult): string {
    return `https://www.openstreetmap.org/?mlat=${encodeURIComponent(position.latitude)}&mlon=${encodeURIComponent(position.longitude)}#map=17/${encodeURIComponent(position.latitude)}/${encodeURIComponent(position.longitude)}`;
  }

  private applyAccessKey(value: AccessKey): void {
    this.accessId = String(value['accessId'] ?? '');
    this.accessKey = String(value['accessKey'] ?? '');
    this.keyUserId = String(value['keyUserId'] ?? '');
    this.systemProvider = String(value['systemProvider'] ?? '');
    this.operation = '';
    this.call.clear();
  }

  private operationPath(operation: LockerOperation): string {
    switch (operation) {
      case 'open':
        return this.endpoints.open;
      case 'lock':
        return this.endpoints.lock;
      case 'position':
        return this.endpoints.position;
      case 'ping':
        return this.endpoints.ping;
    }
  }
}

function isRecord(value: unknown): value is DataRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}